import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Shield, 
  Activity, 
  AlertTriangle,
  Server,
  Database,
  TrendingUp,
  Settings,
  Eye,
  UserPlus,
  ArrowUpRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '@plvplus/core';
import { User } from '../../hooks/useUserRoles';
import { PriPlusOverview } from './widgets/PriPlusOverview';

interface AdminDashboardProps {
  user: User;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ user }) => {
  // Mock admin data
  const systemStats = [
    {
      name: 'Utilisateurs actifs',
      value: '24',
      change: '+3',
      changeType: 'positive' as const,
      icon: Users,
      description: 'Connectés aujourd\'hui'
    },
    {
      name: 'Modules actifs',
      value: '3/5',
      change: '1 en dev',
      changeType: 'neutral' as const,
      icon: Server,
      description: 'Statut système'
    },
    {
      name: 'Sécurité',
      value: '98%',
      change: 'Excellent',
      changeType: 'positive' as const,
      icon: Shield,
      description: 'Score sécurité'
    },
    {
      name: 'Performance',
      value: '1.2s',
      change: '-0.3s',
      changeType: 'positive' as const,
      icon: Activity,
      description: 'Temps de réponse'
    },
    {
      name: 'Stockage',
      value: '78%',
      change: '+12%',
      changeType: 'negative' as const,
      icon: Database,
      description: 'Espace utilisé'
    }
  ];

  const systemAlerts = [
    {
      id: '1',
      type: 'warning',
      title: 'Mise à jour disponible',
      message: 'PLV+ v1.1.0 est disponible avec des corrections de sécurité',
      time: '2h',
      priority: 'medium'
    },
    {
      id: '2',
      type: 'info',
      title: 'Sauvegarde réussie',
      message: 'Sauvegarde automatique des données terminée avec succès',
      time: '6h',
      priority: 'low'
    },
    {
      id: '3',
      type: 'error',
      title: 'Tentative de connexion suspecte',
      message: 'Plusieurs tentatives échouées depuis IP 192.168.1.100',
      time: '1j',
      priority: 'high'
    }
  ];

  const recentUsers = [
    {
      id: '1',
      name: 'Marie Dubois',
      email: 'marie.dubois@plvplus.com',
      role: 'Manager',
      lastLogin: '5 min',
      status: 'online'
    },
    {
      id: '2',
      name: 'Pierre Martin',
      email: 'pierre.martin@plvplus.com',
      role: 'Utilisateur',
      lastLogin: '1h',
      status: 'offline'
    },
    {
      id: '3',
      name: 'Sophie Laurent',
      email: 'sophie.laurent@plvplus.com',
      role: 'Utilisateur',
      lastLogin: '2h',
      status: 'offline'
    }
  ];

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'error': return AlertTriangle;
      case 'warning': return AlertTriangle;
      default: return Activity;
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'error': return 'text-red-600 dark:text-red-400';
      case 'warning': return 'text-yellow-600 dark:text-yellow-400';
      default: return 'text-blue-600 dark:text-blue-400';
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high': return <Badge variant="destructive">Urgent</Badge>;
      case 'medium': return <Badge variant="warning">Moyen</Badge>;
      default: return <Badge variant="secondary">Faible</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-tour="admin-dashboard">
      {/* Admin Header */}
      <div className="bg-gradient-to-r from-red-600 to-pink-600 rounded-lg p-6 text-white" data-tour="admin-welcome">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              Tableau de bord Administrateur 🛡️
            </h1>
            <p className="text-red-100">
              Bienvenue {user.name.split(' ')[0]}, vous avez un accès complet au système PLV+.
            </p>
          </div>
          <div className="hidden md:block">
            <div className="text-right">
              <p className="text-sm text-red-200">Niveau d'accès</p>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                Administrateur
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* System Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-tour="admin-stats">
        {systemStats.map((stat) => (
          <Card key={stat.name} variant="elevated">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {stat.name}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stat.value}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {stat.description}
                  </p>
                </div>
                <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
                  <stat.icon className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="mt-4">
                <span className={`text-sm font-medium ${
                  stat.changeType === 'positive' 
                    ? 'text-green-600 dark:text-green-400' 
                    : stat.changeType === 'negative'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {stat.change}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* PRI+ Overview for Admins */}
      <div className="mb-6" data-tour="admin-pri-overview">
        <PriPlusOverview compact={true} showQuickActions={false} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Alerts */}
        <Card variant="elevated" data-tour="admin-alerts">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5" />
                <span>Alertes système</span>
              </CardTitle>
              <Button variant="outline" size="sm">
                <Eye className="h-4 w-4 mr-2" />
                Voir tout
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {systemAlerts.map((alert) => {
                const Icon = getAlertIcon(alert.type);
                return (
                  <div key={alert.id} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div className={`p-1 rounded-full ${getAlertColor(alert.type)}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {alert.title}
                        </p>
                        {getPriorityBadge(alert.priority)}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {alert.message}
                      </p>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Il y a {alert.time}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Users */}
        <Card variant="elevated">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <Users className="h-5 w-5" />
                <span>Utilisateurs récents</span>
              </CardTitle>
              <Link to="/users">
                <Button variant="outline" size="sm">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Gérer
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 dark:text-primary-400 font-medium text-xs">
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.name}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {user.role} • {user.email}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`w-2 h-2 rounded-full ${
                      user.status === 'online' ? 'bg-green-500' : 'bg-gray-400'
                    }`}></div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {user.lastLogin}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Admin Quick Actions */}
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Actions d'administration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/users"
              className="group block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-red-300 dark:hover:border-red-600"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-500 rounded-lg">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-red-600 dark:group-hover:text-red-400">
                    Utilisateurs
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Gérer les accès
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-400" />
              </div>
            </Link>

            <Link
              to="/settings"
              className="group block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-red-300 dark:hover:border-red-600"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gray-500 rounded-lg">
                  <Settings className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-red-600 dark:group-hover:text-red-400">
                    Configuration
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Paramètres système
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-400" />
              </div>
            </Link>

            <div className="group block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-red-300 dark:hover:border-red-600 cursor-pointer">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Database className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-red-600 dark:group-hover:text-red-400">
                    Base de données
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Maintenance
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-400" />
              </div>
            </div>

            <div className="group block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-red-300 dark:hover:border-red-600 cursor-pointer">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-red-600 dark:group-hover:text-red-400">
                    Rapports
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Analytics système
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-400" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
